// @ts-nocheck
/**
 * 装備強化システム
 *
 * 責務:
 * - 装備レベルアップ
 * - 覚醒（★アップ）
 * - 強化コスト計算
 */

import {
  ENHANCEMENT_TABLE,
  LEVEL_COSTS,
  LEVEL_MULTIPLIERS,
  AWAKENING_COSTS,
  AWAKENING_BONUSES,
  ULTIMATE_AWAKENING_EFFECTS,
  calculateLevelUpCost,
  getAwakeningCost,
  getLevelMultiplier,
  getAwakeningStatBonus,
  getAwakeningEffects,
  getTotalStatMultiplier,
  calculateEnhancedStats,
  canEnhance,
  previewEnhancement,
} from './data/enhancementTable';

import { getRecipe } from './data/recipes';

/**
 * 装備強化クラス
 */
class Enhancer {
  /**
   * @param {Object} alchemyEngine - 親のAlchemyEngineインスタンス
   */
  constructor(alchemyEngine) {
    this.engine = alchemyEngine;

    // 強化履歴
    this.enhanceHistory = [];
  }

  /**
   * 装備を強化
   *
   * @param {Object} equipment - 強化する装備
   * @param {string} enhanceType - 'level' | 'awakening'
   * @returns {{ success: boolean, equipment: Object|null, message: string }}
   */
  enhance(equipment, enhanceType) {
    if (enhanceType === 'level') {
      return this._levelUp(equipment);
    } else if (enhanceType === 'awakening') {
      return this._awaken(equipment);
    }

    return {
      success: false,
      equipment: null,
      message: `無効な強化タイプ: ${enhanceType}`,
    };
  }

  /**
   * レベルアップ
   *
   * @param {Object} equipment - 強化する装備
   * @returns {{ success: boolean, equipment: Object|null, message: string }}
   * @private
   */
  _levelUp(equipment) {
    const { maxLevel } = ENHANCEMENT_TABLE;

    // 最大レベルチェック
    if (equipment.level >= maxLevel) {
      return {
        success: false,
        equipment: null,
        message: '最大レベルに達しています',
      };
    }

    // コスト取得
    const cost = calculateLevelUpCost(equipment.tier, equipment.level);

    // 素材チェック
    const missingMaterials = this._checkMaterials(cost);
    if (missingMaterials.length > 0) {
      return {
        success: false,
        equipment: null,
        missingMaterials,
        message: `素材が不足しています: ${missingMaterials.map(m => m.display).join(', ')}`,
      };
    }

    // 素材消費
    for (const material of cost) {
      this.engine.consumeMaterial(material.type, material.count);
    }

    // レベルアップ
    const newLevel = equipment.level + 1;
    const newStats = this._calculateStats(equipment, newLevel, equipment.awakening);

    const enhanced = {
      ...equipment,
      level: newLevel,
      stats: newStats,
    };

    // 履歴記録
    this._recordEnhancement(equipment, 'level', newLevel);

    return {
      success: true,
      equipment: enhanced,
      previousLevel: equipment.level,
      newLevel,
      statChange: this._calculateStatChange(equipment.stats, newStats),
      message: `${equipment.name}をLv.${newLevel}に強化しました！`,
    };
  }

  /**
   * 覚醒
   *
   * @param {Object} equipment - 強化する装備
   * @returns {{ success: boolean, equipment: Object|null, message: string }}
   * @private
   */
  _awaken(equipment) {
    const { maxAwakening } = ENHANCEMENT_TABLE;

    // 最大覚醒チェック
    if (equipment.awakening >= maxAwakening) {
      return {
        success: false,
        equipment: null,
        message: '最大覚醒に達しています',
      };
    }

    // レベル要件チェック（覚醒にはLv.5以上が必要）
    if (equipment.level < 5) {
      return {
        success: false,
        equipment: null,
        message: '覚醒にはLv.5以上が必要です',
      };
    }

    // コスト取得
    const targetAwakening = equipment.awakening + 1;
    const cost = getAwakeningCost(targetAwakening);

    // 素材チェック
    const missingMaterials = this._checkMaterials(cost);
    if (missingMaterials.length > 0) {
      return {
        success: false,
        equipment: null,
        missingMaterials,
        message: `素材が不足しています: ${missingMaterials.map(m => m.display).join(', ')}`,
      };
    }

    // 素材消費
    for (const material of cost) {
      this.engine.consumeMaterial(material.type, material.count);
    }

    // 覚醒
    const newAwakening = targetAwakening;
    const awakeningBonus = AWAKENING_BONUSES[newAwakening];

    // 新しい効果を追加
    const newEffects = [...equipment.effects];
    if (awakeningBonus?.effects) {
      newEffects.push(...awakeningBonus.effects);
    }

    // ★5覚醒時の究極効果
    if (newAwakening === 5) {
      const ultimateEffect = ULTIMATE_AWAKENING_EFFECTS[equipment.tier];
      if (ultimateEffect) {
        newEffects.push(ultimateEffect);
      }
    }

    // ステータス再計算
    const newStats = this._calculateStats(equipment, equipment.level, newAwakening);

    const enhanced = {
      ...equipment,
      awakening: newAwakening,
      stats: newStats,
      effects: newEffects,
    };

    // 履歴記録
    this._recordEnhancement(equipment, 'awakening', newAwakening);

    return {
      success: true,
      equipment: enhanced,
      previousAwakening: equipment.awakening,
      newAwakening,
      statBonus: awakeningBonus?.statBonus || 0,
      newEffects: awakeningBonus?.effects || [],
      isUltimate: newAwakening === 5,
      message: `${equipment.name}を★${newAwakening}に覚醒しました！`,
    };
  }

  /**
   * 素材チェック
   *
   * @param {Object[]} requiredMaterials - 必要素材リスト
   * @returns {Object[]} 不足素材リスト
   * @private
   */
  _checkMaterials(requiredMaterials) {
    const missing = [];
    for (const material of requiredMaterials) {
      const have = this.engine.getMaterialCount(material.type);
      if (have < material.count) {
        missing.push({
          type: material.type,
          need: material.count,
          have,
          shortage: material.count - have,
          display: `${material.type}(${have}/${material.count})`,
        });
      }
    }
    return missing;
  }

  /**
   * ステータスを計算
   *
   * @param {Object} equipment - 装備
   * @param {number} level - レベル
   * @param {number} awakening - 覚醒
   * @returns {Object} 計算後のステータス
   * @private
   */
  _calculateStats(equipment, level, awakening) {
    // 基礎ステータスを取得（装備に保存された基礎値またはレシピから）
    const baseStats = equipment.baseStats ||
      getRecipe(equipment.recipeId)?.baseStats ||
      equipment.stats;

    return calculateEnhancedStats(baseStats, level, awakening);
  }

  /**
   * ステータス変化量を計算
   *
   * @param {Object} oldStats - 変更前のステータス
   * @param {Object} newStats - 変更後のステータス
   * @returns {Object} 変化量
   * @private
   */
  _calculateStatChange(oldStats, newStats) {
    const change = {};
    for (const [stat, newValue] of Object.entries(newStats)) {
      const oldValue = oldStats[stat] || 0;
      change[stat] = {
        old: oldValue,
        new: newValue,
        diff: newValue - oldValue,
        percent: oldValue > 0 ? ((newValue - oldValue) / oldValue * 100).toFixed(1) : 'N/A',
      };
    }
    return change;
  }

  /**
   * 強化履歴を記録
   * @private
   */
  _recordEnhancement(equipment, type, newValue) {
    this.enhanceHistory.push({
      equipmentId: equipment.id,
      equipmentName: equipment.name,
      type,
      previousValue: type === 'level' ? equipment.level : equipment.awakening,
      newValue,
      timestamp: Date.now(),
    });

    // 履歴は直近100件のみ保持
    if (this.enhanceHistory.length > 100) {
      this.enhanceHistory.shift();
    }
  }

  /**
   * 強化プレビュー
   *
   * @param {Object} equipment - 装備
   * @param {string} enhanceType - 'level' | 'awakening'
   * @returns {Object|null} プレビュー情報
   */
  previewEnhance(equipment, enhanceType) {
    const preview = previewEnhancement(equipment, enhanceType);
    if (!preview) return null;

    // 素材状況を追加
    const costWithStatus = preview.cost.map(m => ({
      ...m,
      have: this.engine.getMaterialCount(m.type),
      isSufficient: this.engine.getMaterialCount(m.type) >= m.count,
    }));

    const missingMaterials = costWithStatus.filter(m => !m.isSufficient);

    // 強化可能かチェック
    const canEnhanceResult = canEnhance(equipment, enhanceType);

    return {
      ...preview,
      cost: costWithStatus,
      missingMaterials,
      canEnhance: canEnhanceResult.canEnhance && missingMaterials.length === 0,
      reason: canEnhanceResult.reason,
    };
  }

  /**
   * 最大強化までの累計コストを取得
   *
   * @param {Object} equipment - 装備
   * @returns {Object} 累計コスト情報
   */
  getTotalCostToMax(equipment) {
    const { maxLevel, maxAwakening } = ENHANCEMENT_TABLE;

    // レベルアップ累計
    const levelCosts = {};
    for (let level = equipment.level; level < maxLevel; level++) {
      const cost = calculateLevelUpCost(equipment.tier, level);
      for (const mat of cost) {
        levelCosts[mat.type] = (levelCosts[mat.type] || 0) + mat.count;
      }
    }

    // 覚醒累計
    const awakeningCosts = {};
    for (let awakening = equipment.awakening + 1; awakening <= maxAwakening; awakening++) {
      const cost = getAwakeningCost(awakening);
      for (const mat of cost) {
        awakeningCosts[mat.type] = (awakeningCosts[mat.type] || 0) + mat.count;
      }
    }

    // 合計
    const totalCosts = { ...levelCosts };
    for (const [type, count] of Object.entries(awakeningCosts)) {
      totalCosts[type] = (totalCosts[type] || 0) + count;
    }

    return {
      currentLevel: equipment.level,
      currentAwakening: equipment.awakening,
      targetLevel: maxLevel,
      targetAwakening: maxAwakening,
      levelCosts,
      awakeningCosts,
      totalCosts,
    };
  }

  /**
   * 強化履歴を取得
   *
   * @returns {Object[]} 強化履歴
   */
  getEnhanceHistory() {
    return [...this.enhanceHistory];
  }

  /**
   * 装備が強化可能か一括チェック
   *
   * @param {Object} equipment - 装備
   * @returns {Object} 各強化タイプの可否
   */
  getEnhanceAvailability(equipment) {
    const levelCheck = canEnhance(equipment, 'level');
    const awakeningCheck = canEnhance(equipment, 'awakening');

    const levelCost = equipment.level < ENHANCEMENT_TABLE.maxLevel
      ? calculateLevelUpCost(equipment.tier, equipment.level)
      : [];
    const awakeningCost = equipment.awakening < ENHANCEMENT_TABLE.maxAwakening
      ? getAwakeningCost(equipment.awakening + 1)
      : [];

    const levelMissing = this._checkMaterials(levelCost);
    const awakeningMissing = this._checkMaterials(awakeningCost);

    return {
      level: {
        ...levelCheck,
        canEnhance: levelCheck.canEnhance && levelMissing.length === 0,
        cost: levelCost,
        missingMaterials: levelMissing,
        hasMaterials: levelMissing.length === 0,
      },
      awakening: {
        ...awakeningCheck,
        canEnhance: awakeningCheck.canEnhance && awakeningMissing.length === 0,
        cost: awakeningCost,
        missingMaterials: awakeningMissing,
        hasMaterials: awakeningMissing.length === 0,
      },
    };
  }

  /**
   * 状態のシリアライズ
   *
   * @returns {Object} シリアライズされた状態
   */
  serialize() {
    return {
      enhanceHistory: this.enhanceHistory,
    };
  }

  /**
   * 状態のデシリアライズ
   *
   * @param {Object} data - シリアライズされた状態
   */
  deserialize(data) {
    if (data.enhanceHistory) {
      this.enhanceHistory = data.enhanceHistory;
    }
  }
}

export default Enhancer;
